import { useEffect, useState } from "react";
import { Lock, Upload, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { blobUrl } from "@/lib/scanOptions";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MyAgreements } from "@/pages/MyAgreementsPage";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { REMIND_OPTIONS } from "@/lib/plannerOptions";
import { toast } from "sonner";

const LogoCard = () => {
  const { hasFeature } = useAuth();
  const [src, setSrc] = useState(null);
  const gold = hasFeature("CUSTOM_LOGO");
  const load = () => blobUrl("/office-settings/logo").then(setSrc).catch(() => setSrc(null));
  useEffect(() => { if (gold) load(); }, [gold]);
  const upload = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post("/office-settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Logo yüklendi"); load(); }
    catch (x) { toast.error(formatApiError(x.response?.data?.detail?.msg || x.response?.data?.detail)); }
  };
  const remove = async () => { await api.delete("/office-settings/logo"); setSrc(null); toast.success("Logo kaldırıldı"); };

  return (
    <Card className="p-5 space-y-3" data-testid="logo-card">
      <div className="font-semibold text-slate-900">Belge logosu</div>
      <p className="text-sm text-slate-500">Satış sözleşmesi, kayıt formu ve tapu takip PDF'lerinin üst kısmında görünür.</p>
      {!gold ? (
        <div data-testid="logo-locked" className="rounded-lg bg-slate-50 border p-4 text-sm flex items-center justify-between gap-3">
          <span className="flex items-center gap-2"><Lock className="w-4 h-4" />Paketinizde belgelerde EmlakPro logosu kullanılır. Kendi logonuz için Gold pakete geçin.</span>
          <Link to="/subscription"><Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">Paketi Yükselt</Button></Link>
        </div>
      ) : (
        <div className="flex items-center gap-4">
          <div className="w-40 h-20 rounded-lg border bg-white flex items-center justify-center overflow-hidden">{src ? <img data-testid="logo-preview" src={src} alt="logo" className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-slate-400">EmlakPro logosu</span>}</div>
          <label className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 cursor-pointer"><Upload className="w-4 h-4" />{src ? "Değiştir" : "Logo yükle"}
            <input data-testid="logo-upload-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} /></label>
          {src && <Button size="sm" variant="ghost" data-testid="logo-remove-btn" onClick={remove}><Trash2 className="w-4 h-4 text-red-600" /></Button>}
        </div>
      )}
    </Card>
  );
};

const NotesCard = () => {
  const { hasFeature } = useAuth();
  const [notes, setNotes] = useState("");
  useEffect(() => { api.get("/office-settings").then((r) => setNotes(r.data.deed_footer_notes || "")); }, []);
  const save = async () => {
    try { await api.put("/office-settings", { deed_footer_notes: notes }); toast.success("Notlar kaydedildi"); } catch (x) { toast.error(formatApiError(x.response?.data?.detail)); }
  };
  if (!hasFeature("DEED")) return <Card className="p-5 text-sm text-slate-500 flex items-center gap-2" data-testid="deed-notes-locked"><Lock className="w-4 h-4" />Tapu Takip paketinizde bulunmuyor.</Card>;
  return (
    <Card className="p-5 space-y-3" data-testid="deed-notes-card">
      <div className="font-semibold text-slate-900">Tapu takip PDF sabit notları</div>
      <p className="text-sm text-slate-500">Her satır, tapu takip PDF'inin en alt bölümünde ayrı bir not olarak yer alır (en fazla 12 satır).</p>
      <Textarea data-testid="deed-notes-input" rows={5} maxLength={1500} value={notes} onChange={(e) => setNotes(e.target.value.split("\n").slice(0, 12).join("\n"))} placeholder={"Örn. Randevudan 30 dakika önce tapu müdürlüğünde olunuz.\nKimlik kartlarınızın asıllarını yanınızda getiriniz."} />
      <div className="flex justify-end"><Button data-testid="deed-notes-save" onClick={save}>Kaydet</Button></div>
    </Card>
  );
};

const PlannerCard = ({ part }) => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/planner/settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const save = async () => {
    try { setS((await api.put("/planner/settings", { daily_digest: s.daily_digest, default_remind: s.default_remind, weights: s.weights })).data); toast.success("Ayarlar kaydedildi"); }
    catch (x) { toast.error(formatApiError(x.response?.data?.detail)); }
  };
  return (
    <Card className="p-5 space-y-4" data-testid={`planner-${part}-card`}>
      {part === "calendar" ? (
        <>
          <div className="font-semibold">Takvim ve hatırlatmalar</div>
          <label className="flex items-center gap-2 text-sm"><Switch data-testid="daily-digest-switch" checked={s.daily_digest} onCheckedChange={(v) => setS({ ...s, daily_digest: v })} />Yaklaşan randevuları günde bir kez pop-up ile hatırlat</label>
          <div className="max-w-xs">
            <Label>Yeni kayıtlarda varsayılan hatırlatma</Label>
            <Select value={s.default_remind == null ? "none" : String(s.default_remind)} onValueChange={(v) => setS({ ...s, default_remind: v === "none" ? null : Number(v) })}>
              <SelectTrigger data-testid="default-remind-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(REMIND_OPTIONS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </>
      ) : (
        <>
          <div className="font-semibold">Performans puanları</div>
          <p className="text-sm text-slate-500">Raporlarda ve haftanın danışmanı seçiminde her işlemin kaç puan değerinde olduğunu belirleyin.</p>
          <div className="grid md:grid-cols-2 gap-3">{Object.entries(s.labels).map(([k, l]) => (
            <div key={k} className="flex items-center justify-between gap-3 rounded-lg border p-2.5"><span className="text-sm">{l}</span>
              <Input data-testid={`weight-${k}`} type="number" min="0" className="w-20" value={s.weights[k]} onChange={(e) => setS({ ...s, weights: { ...s.weights, [k]: Number(e.target.value) } })} /></div>
          ))}</div>
        </>
      )}
      <div className="flex justify-end"><Button data-testid={`planner-${part}-save`} onClick={save}>Kaydet</Button></div>
    </Card>
  );
};

export default function OfficeSettingsPage() {
  return (
    <div className="space-y-6 max-w-3xl" data-testid="office-settings-page">
      <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Ayarlar</h1>
      <Tabs defaultValue="docs">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="docs" data-testid="settings-tab-docs">Belgeler & Logo</TabsTrigger>
          <TabsTrigger value="deed" data-testid="settings-tab-deed">Tapu Takip</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="settings-tab-calendar">Takvim & Hatırlatmalar</TabsTrigger>
          <TabsTrigger value="performance" data-testid="settings-tab-performance">Performans</TabsTrigger>
          <TabsTrigger value="legal" data-testid="settings-tab-legal">Sözleşmelerim ve Onaylarım</TabsTrigger>
        </TabsList>
        <TabsContent value="legal" className="mt-4"><MyAgreements /></TabsContent>
        <TabsContent value="docs" className="mt-4"><LogoCard /></TabsContent>
        <TabsContent value="deed" className="mt-4"><NotesCard /></TabsContent>
        <TabsContent value="calendar" className="mt-4"><PlannerCard part="calendar" /></TabsContent>
        <TabsContent value="performance" className="mt-4"><PlannerCard part="performance" /></TabsContent>
      </Tabs>
    </div>
  );
}
